import { useDocumentVisibility, useInterval } from '@mantine/hooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { BlackbaudRaisersEdgeIntegration } from '@feathr/blackbox';
import { submitModelWithErrorHandling, toast } from '@feathr/components';
import { errorMessage, logUserEvents } from '@feathr/hooks';

import { isFundMissing } from './RaisersEdgeActivitiesTab/RaisersEdgeActivitiesTab.utils';
import { SOLICIT_CODE_KEY, UNSUBSCRIBE_ACTION_KEY } from './raisersEdgeConfig';
import type { IRaisersEdgeMappingDraft } from './RaisersEdgeMappingsTab/RaisersEdgeMappingsTab.hooks';

const VALIDATED_ATTRIBUTES = [UNSUBSCRIBE_ACTION_KEY, SOLICIT_CODE_KEY].map(
  (key) => `unsubscribe_sync_preference.${key}`,
);

interface IRaisersEdgeSave {
  /** Whether a staged attribute or mapping has unsaved changes. Other dirty attributes don't count. */
  isDirty: boolean;
  isSaving: boolean;
  /** Persists the changed staged attributes in one PATCH, then the staged mappings. */
  handleSave: () => Promise<void>;
  /** The first validation error of the staged changes, which disables Save & Sync. */
  saveDisabledReason?: string;
}

/**
 * Page-level Save & Sync for the changes the tabs stage on the integration and the mapping draft.
 * Leaving the page discards the staged attributes; the draft is page state and drops with it.
 */
export function useRaisersEdgeSave(
  integration: BlackbaudRaisersEdgeIntegration | undefined,
  mappingDraft: IRaisersEdgeMappingDraft,
): IRaisersEdgeSave {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  // State lags a render behind; the ref blocks a second click that lands before the button disables.
  const isSavingRef = useRef(false);
  // A disconnected integration hides its tabs, so its staged edits can't be saved.
  const isActive = !!integration?.isActive;
  const changedAttributes = integration && isActive ? integration.changedStagedAttributes : [];
  const hasMappingChanges = isActive && mappingDraft.isDirty;
  const isDirty = changedAttributes.length > 0 || hasMappingChanges;
  // Person upsync shares `upsync_activities`, so compare donations alone. A clean attribute has no shadow.
  const upsync = integration?.get('upsync_activities');
  const savedUpsync = integration?.shadowAttributes.upsync_activities ?? upsync;
  const isDonationUpsyncOn = !!upsync?.donations;
  const isDonationUpsyncChanged = isDonationUpsyncOn !== !!savedUpsync?.donations;

  // The staged attributes live on the shared model, so leaving the page has to drop them.
  useEffect(() => (): void => integration?.discardStagedChanges(), [integration]);

  function getSaveDisabledReason(): string | undefined {
    if (!integration || !isDirty) {
      return undefined;
    }
    if (hasMappingChanges && mappingDraft.saveDisabledReason) {
      return mappingDraft.saveDisabledReason;
    }
    // `isValid` is memoized; `validate` is not, so only run it to read the message.
    if (!integration.isValid(VALIDATED_ATTRIBUTES, false)) {
      return integration.validate(VALIDATED_ATTRIBUTES, false).errors[0];
    }
    // Only a save that touches donation upsync needs a fund, so a bad saved state can't block others.
    const touchesDonations = isDonationUpsyncChanged || changedAttributes.includes('fundraising');
    if (
      touchesDonations &&
      isFundMissing({
        fundraising: integration.get('fundraising'),
        upsync_activities: integration.get('upsync_activities'),
      })
    ) {
      return t('Select a fund to sync donations to Blackbaud.');
    }
    return undefined;
  }

  // Same event as v1, sent once the PATCH that turns donation upsync on has saved.
  const turnsOnDonationUpsync = isDonationUpsyncChanged && isDonationUpsyncOn;

  function logDonationUpsync(): void {
    if (integration && turnsOnDonationUpsync) {
      logUserEvents({ integrated_renxt_fundraising: { account_id: integration.get('account') } });
    }
  }

  async function handleSave(): Promise<void> {
    if (!integration || isSavingRef.current) {
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);
    try {
      const isPatched =
        !changedAttributes.length ||
        (await submitModelWithErrorHandling({
          operation: integration.patch(integration.getStagedPatch()),
          onSuccess: logDonationUpsync,
          t,
        }));
      if (!isPatched) {
        return;
      }
      if (hasMappingChanges) {
        try {
          await mappingDraft.save();
        } catch (error) {
          // The server may have saved some of the changes before one failed.
          toast(
            t("Couldn't save every mapping. Check the list and try again. {{- error}}", {
              error: errorMessage(error, t),
            }),
            { type: 'error' },
          );
          return;
        }
      }
      toast(t('Your changes have been saved.'), { type: 'success' });
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  }

  return { handleSave, isDirty, isSaving, saveDisabledReason: getSaveDisabledReason() };
}

export const POLL_INTERVAL_MS = 60_000;

/**
 * Counts polls of the page's sync data: one per interval while `enabled` and the tab is visible,
 * and one when the tab comes back into view. Starts at 0; the data's owners do the first load.
 */
export function useRaisersEdgePollCount(enabled: boolean, intervalMs = POLL_INTERVAL_MS): number {
  const [pollCount, setPollCount] = useState(0);
  const isVisible = useDocumentVisibility() === 'visible';
  const shouldPoll = enabled && isVisible;
  // Stable, because `useInterval` restarts the interval each time its callback changes.
  const countPoll = useCallback(() => setPollCount((count) => count + 1), []);
  const interval = useInterval(countPoll, intervalMs);
  const wasHidden = useRef(false);

  useEffect(() => {
    if (!shouldPoll) {
      return undefined;
    }
    interval.start();
    return interval.stop;
  }, [shouldPoll]);

  // Catch up on what changed while the tab was hidden.
  useEffect(() => {
    if (isVisible && wasHidden.current && enabled) {
      countPoll();
    }
    wasHidden.current = !isVisible;
  }, [isVisible]);

  return pollCount;
}
